/**
 * Chuẩn hoá response list/detail từ backend (axios `response.data`), dùng chung mọi module API.
 * Hỗ trợ dạng `{ data | results, paging?, total? }` hoặc mảng thuần.
 */

/** Kết quả danh sách sau khi chuẩn hoá */
export type PagedListResult<T> = {
  data: T[]
  paging: { page: number; pageSize: number; total: number }
  /** Tổng theo toàn bộ bản ghi khớp lọc (không chỉ trang hiện tại) — dòng tổng trên đầu bảng. */
  summary?: ListSummary
}

export type ListSummary = Record<string, number>

/** Body JSON list thường gặp (Nest transform, v.v.) */
export type ApiListResponse<T> = {
  data?: T[]
  results?: T[]
  paging?: { page: number; pageSize: number; total: number }
  total?: number
  summary?: unknown
}

/** Lấy `summary` phẳng (key → số) từ body list; field không phải số bị bỏ. */
export const pickListSummary = (res: unknown): ListSummary | undefined => {
  const envelope = res as { summary?: unknown; thongKe?: unknown } | null | undefined
  // Một số module cũ trả `thongKe`; chuẩn list hiện tại / PRD dùng `summary`.
  const raw = envelope?.summary ?? envelope?.thongKe
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const out: ListSummary = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN
    if (Number.isFinite(n)) out[k] = n
  }
  return out
}

export const normalizeListResponse = <T>(res: ApiListResponse<T> | T[]): PagedListResult<T> => {
  if (Array.isArray(res)) {
    return {
      data: res,
      paging: { page: 1, pageSize: res.length || 25, total: res.length },
    }
  }

  const data = Array.isArray(res?.data) ? res.data : Array.isArray(res?.results) ? res.results : []
  const paging = res?.paging ?? {
    page: 1,
    pageSize: data.length || 25,
    total: typeof res?.total === 'number' ? res.total : data.length,
  }

  const summary = pickListSummary(res)
  return {
    data,
    paging: {
      page: paging.page ?? 1,
      pageSize: paging.pageSize ?? 25,
      total: paging.total ?? data.length,
    },
    ...(summary ? { summary } : {}),
  }
}

/** Lấy payload `data` khi backend bọc `{ data: entity }` */
export const normalizeDetailResponse = <T>(res: T | { data?: T }): T => {
  if (res && typeof res === 'object' && 'data' in (res as Record<string, unknown>)) {
    return ((res as { data?: T }).data ?? res) as T
  }
  return res as T
}
